import { Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ALL } from '../../../../../common/constants/app-constants';
import { EventHandler } from '../../../../../common/services/eventHandler';
import { AppSession } from '../../../../../common/values/appSession';
import { ProviderAffiliationHandler } from '../../../classes/providerAffiliationHandler';
import { ProviderCard, ProviderCardNavigation } from '../../../constants/result';
import { IProvider } from '../../../interfaces/iProvider';
import { IAffiliationFilter, IAffiliationProvider, IProviderAffiliationGroup } from '../../../interfaces/iProviderAffiliation';
import { IDetail } from '../../../interfaces/iProviderDetail';
import { ProviderDetail } from '../../../services/providerDetail';
import { BaseComponent } from './../../../../common/components/core/baseCmp';

@Component({
  moduleId: module.id,
  selector: 'app-fc-affiliation-card-cmp',
  templateUrl: './affiliationCardCmp.html'
})
export class AffiliationCardComponent extends BaseComponent implements OnChanges {
  @Output() cardSelected: EventEmitter<ProviderCard> = new EventEmitter<ProviderCard>();
  @Input() providerAffiliation: Map<string, IProviderAffiliationGroup>;
  affiliationContent = this.content?.result?.affiliationCard;
  overviewContent = this.content?.result?.providerOverview;
  @Output() cardNavigation: EventEmitter<ProviderCardNavigation> = new EventEmitter<ProviderCardNavigation>();
  @Input() providerDetails: IDetail;
  @Output() closeProviderCard: EventEmitter<boolean> = new EventEmitter();
  @Input() showSearchResultsFilter: boolean;
  cardProvider: IProvider;
  @Output() showLoader: EventEmitter<boolean> = new EventEmitter<boolean>();
  showAffiliationFilter = false;
  affiliationFilter = {} as IAffiliationFilter;
  defaultFilterValue = ALL;
  filterOptionCount: number;
  filteredAffiliations: Map<string, IProviderAffiliationGroup>;

  constructor(
    private _route: ActivatedRoute,
    private _eventHandler: EventHandler,
    @Inject(AppSession)
    private _appSession: AppSession,
    private _providerDetail: ProviderDetail,
    private _affiliationHandler: ProviderAffiliationHandler
  ) {
    super(_route, _eventHandler, _appSession);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.showSearchResultsFilter && this._affiliationHandler?.affiliationFilter) {
      this.affiliationFilter = Object.assign({}, this._affiliationHandler.affiliationFilter);
    }
    this.setFilterCount();
  }

  /**
   * Method to Close Speciality Card.
   * Hiding Speciality Card and Provider Details Card.
   */
  onCloseCard() {
    this.closeProviderCard.emit(true);
  }

  /**
   * Method to show Previous Card State in Provider Tab.
   * Hiding Affiliations List and Showing Provider Card.
   */
  onShowPreviousState(): void {
    if (!this.showSearchResultsFilter) {
      this._affiliationHandler.affiliationFilter = {} as IAffiliationFilter;
    }
    this.cardSelected.emit(ProviderCard.OVERVIEW);
  }

  /**
   * Method to show Affiliate Card  in Provider Tab.
   * Showing Provider Card based on provider type.
   */
  onShowAffiliatedProviderCard(provider: IAffiliationProvider): void {
    this._affiliationHandler.affiliationFilter = Object.assign({}, this.affiliationFilter);
    this.cardSelected.emit(ProviderCard.OVERVIEW);
    this.showLoader.emit(true);
    this.cardNavigation.emit(ProviderCardNavigation.AFFILIATION);
    this._providerDetail.initialize(provider.providerIdentifier, provider.addressSummary.addressIdentifier, this.cardProvider);
  }

  /** Function to get Tier name text */
  getTierText(tierName: string): string {
    if (this.affiliationContent?.preferredProvider) {
      return this.affiliationContent.preferredProvider.replace('{tierName}', tierName);
    }
    return tierName;
  }

  onShowAffiliationFilter() {
    this.showAffiliationFilter = true;
  }

  onApplyAndCloseAffiliationFilter(event) {
    if (event) {
      this.affiliationFilter = event;
      this.setFilterCount();
    }
    this.showAffiliationFilter = false;
  }

  setFilterCount() {
    const filterKeys = ['filterType', 'specialtyCode'];
    this.filterOptionCount = filterKeys.reduce((count, key) => {
      const value = this.affiliationFilter?.[key];
      return count + (value && value !== this.defaultFilterValue ? 1 : 0);
    }, 0);
    this.filteredAffiliations = this.filterProviderAffiliations();
  }

  filterProviderAffiliations(): Map<string, IProviderAffiliationGroup> {
    if (this.providerAffiliation?.size === 0 || this.providerAffiliation?.size === undefined) {
      return new Map<string, IProviderAffiliationGroup>();
    }
    // If no filter or filter is default, return all affiliations
    if (!this.affiliationFilter?.filterType || this.affiliationFilter.filterType === this.defaultFilterValue) {
      return this.providerAffiliation;
    }

    // Get the filtered affiliation group
    const filteredGroup = this.providerAffiliation.get(this.affiliationFilter.filterType);

    // If specialtyCode is set and not default, filter the providers by specialty
    if (filteredGroup && this.affiliationFilter?.specialtyCode && this.affiliationFilter?.specialtyCode !== this.defaultFilterValue) {
      const filteredProviders = (filteredGroup.providers || []).filter((provider) => (provider.specialtyList || []).some((specialty) => specialty.code === this.affiliationFilter.specialtyCode));
      // Return a new Map with only the filtered group
      const result = new Map<string, IProviderAffiliationGroup>();
      result.set(this.affiliationFilter.filterType, {
        ...filteredGroup,
        providers: filteredProviders
      });
      return result;
    }

    // Otherwise, return a Map with only the filtered group
    if (filteredGroup) {
      const result = new Map<string, IProviderAffiliationGroup>();
      result.set(this.affiliationFilter.filterType, filteredGroup);
      return result;
    }

    // If nothing matches, return an empty Map
    return new Map<string, IProviderAffiliationGroup>();
  }

  showAffiliationFilterButton(): boolean {
    return this.providerAffiliation?.size > 1 || (this.providerAffiliation?.size === 1 && Array.from(this.providerAffiliation.values())[0]?.specialities?.length > 1);
  }
}
